/*
 * Copyright (c) 2021, 2022 Tropyc Stoic (38c316a1b8d7188d059c273d892b723b)
 *
 * Permission to use, copy, modify, and distribute this software for any
 * purpose with or without fee is hereby granted, provided that the above
 * copyright notice and this permission notice appear in all copies.
 *
 * THE SOFTWARE IS PROVIDED "AS IS" AND THE AUTHOR DISCLAIMS ALL WARRANTIES
 * WITH REGARD TO THIS SOFTWARE INCLUDING ALL IMPLIED WARRANTIES OF
 * MERCHANTABILITY AND FITNESS. IN NO EVENT SHALL THE AUTHOR BE LIABLE FOR
 * ANY SPECIAL, DIRECT, INDIRECT, OR CONSEQUENTIAL DAMAGES OR ANY DAMAGES
 * WHATSOEVER RESULTING FROM LOSS OF USE, DATA OR PROFITS, WHETHER IN AN
 * ACTION OF CONTRACT, NEGLIGENCE OR OTHER TORTIOUS ACTION, ARISING OUT OF
 * OR IN CONNECTION WITH THE USE OR PERFORMANCE OF THIS SOFTWARE.
 */
import { useState, createContext, useContext } from 'react';
import { ethers, BigNumber} from 'ethers'
import BigNumberJS from 'bignumber.js'
import Airdrop from './artifacts/contracts/Airdrop.sol/Airdrop.json'
import ECRDummy from './artifacts/contracts/ECRDummy.sol/ECRDummy.json'

BigNumberJS.set({ ROUNDING_MODE: BigNumberJS.ROUND_DOWN })

const Web3Context = createContext()
const Web3Provider = Web3Context.Provider

const AirdropContext = createContext()
const AirdropProvider = AirdropContext.Provider

function Allowance(props) {
    //amount is human readable, not raw; BigNumberJS
    const {token, amount, airdropHandler} = props;

    const web3 = useContext(Web3Context);
    const airdrop = useContext(AirdropContext);

    const [allowance, setAllowance] = useState();
    const [rawAmount, setRawAmount] = useState();
    const [approving, setApproving] = useState(false);
    const [airdropping, setAirdropping] = useState(false);
    const [airdropped, setAirdropped] = useState(false);

    async function loadAllowance(tokenAddress) {
        const signer = web3.getSigner()
        const signerAddress = await signer.getAddress();

        const tokenContract = new ethers.Contract(tokenAddress, ECRDummy.abi, web3)

        const allowance = await tokenContract.allowance(signerAddress, airdrop);
        const digits = await tokenContract.decimals();

        return [allowance, digits];
    }

    if (!allowance) {
        loadAllowance(token).then(([a, digits]) => {
            setAllowance(a);
            //the horror: 2 BigNumber classes?!
            setRawAmount(BigNumber.from(amount.multipliedBy(BigNumberJS(10).pow(digits)).toFixed()));
        });
    }

    async function approve(event) {
        event.preventDefault();

        const signer = web3.getSigner()
        const contract = new ethers.Contract(token, ECRDummy.abi, signer)
        setApproving(true);
        try {
            const approve = await contract.approve(airdrop, ethers.constants.MaxUint256)
            if (approve) {
                //reload allowance
                const [a, ] = await loadAllowance(token);
                setAllowance(a);
            }
        } finally {
            setApproving(false);
        }
    }

    if (approving) {
        return (
            <div className="col-12">
                <button type="submit" className="btn btn-primary disabled">Approving...</button>
            </div>
        )
    }

    async function doAirdrop(event) {
        event.preventDefault();

        setAirdropping(true);
        try{
            await airdropHandler();
            setAirdropped(true);
        } finally {
            setAirdropping(false);
        }
    }

    if (airdropping) {
        return (
            <>
            <div className="col-6">
                    <button type="submit" className="btn btn-primary disabled">Approved</button>
                </div>
            <div className="col-6">
                <button type="submit" className="btn btn-primary disabled">Airdropping...</button>
            </div>
            </>
        )
    }
    if (airdropped) {
        return (
            <>
            <div className="col-6">
                    <button type="submit" className="btn btn-primary disabled">Approved</button>
                </div>
            <div className="col-6">
                <button type="submit" className="btn btn-primary disabled">Airdropped !</button>
            </div>
            </>
        )
    }

    return (rawAmount && rawAmount.lt(allowance)) ?
                <>
                <div className="col-6">
                    <button type="submit" className="btn btn-primary disabled">Approved</button>
                </div>
                <div className="col-6">
                    <button type="submit" className="btn btn-primary" onClick={doAirdrop}>Airdrop</button>
                </div>
                </>
                :
                <div className="col-12">
                {
                (rawAmount && rawAmount.gte(allowance)) ?
                    <button type="submit" className="btn btn-primary" onClick={approve}>Approve</button>
                    :
                    <button type="submit" className="btn btn-primary disabled">Airdrop</button>
                }
                </div>
}

function Balance(props) {
    const {token} = props;

    const [balance, setBalance] = useState();

    const web3 = useContext(Web3Context);

    async function loadBalance(tokenAddress) {
        const signer = web3.getSigner()
        const signerAddress = await signer.getAddress();

        const tokenContract = new ethers.Contract(tokenAddress, ECRDummy.abi, web3)

        const balance = await tokenContract.balanceOf(signerAddress);
        const symbol = await tokenContract.symbol();
        const digits = await tokenContract.decimals();
        const name = await tokenContract.name();

        return balance.div(BigNumber.from(10).pow(digits)).toString()+ " " + symbol + " (" + name + ")";
    }

    if (!balance)
        loadBalance(token).then(v => setBalance(v));

    return <>
        Your balance is: {balance? balance : "(loading...)"}
        </>;
 }

function AirdropComponent(props) {
    const {web3, airdrop} = props;

    const [airdropInfo, setAirdropInfo] = useState({
        token: "",
        amount: 0,
        addresses: "",
    });

    let addressCount = 0;
    let uniqueAddresses = [];
    let uniqueAddressesAmount = []
    let totalAmount = BigNumberJS(0); //BigNumberJS(airdropInfo.amount).multipliedBy(addressCount);
    if (airdropInfo.addresses) {
        const addresses = airdropInfo.addresses.split(/\r?\n/);
        const addressWithAmount = addresses.map(a => {
              const ta = a.trim();
              for (const separator of [',', '\t', ' ']) {
                  const delimited = a.split(separator)
                          .map(a => a.trim())
                          .filter(x => x.length > 0);
                  if (delimited.length > 1) {
                    //TODO: exclude hexa amounts? 0x probably means another address
                      return [delimited[0], BigNumberJS(delimited[1], 10)];
                  }
              }
              return [ta, BigNumberJS(airdropInfo.amount)];
            })
            .filter(pair => ethers.utils.isAddress(pair[0]))
            .filter(pair => !pair[1].isNaN())
            .map(pair => [ethers.utils.getAddress(pair[0]), pair[1]]);
        const addressSet = new Set();
        for (const addressPair of addressWithAmount) {
            if (!addressSet.has(addressPair[0])) {
                addressSet.add(addressPair[0]);

                uniqueAddresses.push(addressPair[0]);
                uniqueAddressesAmount.push(addressPair[1]);
                totalAmount = totalAmount.plus(addressPair[1]);
            }
        }
        addressCount = addressSet.size;
    }

    function bumpState(newStateChunk) {
        setAirdropInfo(prev => {return {
                ...prev,
                ...newStateChunk
        }});
    }

    function handleInputChange(event) {
        const target = event.target;
        const value = target.value;
        const name = target.name;

        if (name === "amount") {
            if (/*value.trim().length === 0 || */ isNaN(value)) {
                //ignore non-numeric values
                return;
            }
        }

        bumpState({[name]: value})
    }

    async function airdropIt() {
        const signer = web3.getSigner()

        const tokenContract = new ethers.Contract(airdropInfo.token, ECRDummy.abi, web3)

        const tokenDigits = await tokenContract.decimals();

        const protocol = new ethers.Contract(airdrop, Airdrop.abi, signer)
        const tx = await protocol.airdrop(airdropInfo.token, uniqueAddresses, uniqueAddressesAmount
            .map(a => BigNumber.from(BigNumberJS(a).multipliedBy(BigNumberJS(10).pow(tokenDigits)).toFixed())));
        /* const receipt = */
        await tx.wait();

//        const error = await web3.call(receipt.transactionHash, receipt.blockNumber);
//        const message  = ethers.utils.toUtf8String('0x' + error.substr(138));
    }

    const addressesPlaceholder = "Example with 567 custom amount:\n0x498e4bfc9b3a4e298a64aa8de3bf69b82d1948b1 567\n\nDefault:\n0x498e4bfc9b3a4e298a64aa8de3bf69b82d1948b1\n\nThis example will go away once you start typing."

    return (
        <Web3Provider value={web3}>
        <AirdropProvider value={airdrop}>
        <div className="card">
          <div className="card-header">
              Airdrop
          </div>
          <div className=" m-1 p-3">
        <form className="row g-3">
          <div className="col-md-6">
            <label htmlFor="amount" className="form-label">Token</label>
            <input type="text" className="form-control" id="token" name="token" value={airdropInfo.token} onChange={handleInputChange}/>
          </div>
          <div className="col-md-6">
            <label htmlFor="amount" className="form-label">Amount you want to drop <b>per account</b></label>
            <input type="number" className="form-control" id="amount" name="amount" value={airdropInfo.amount} onChange={handleInputChange}/>
          </div>
          <div className="col-12">
            <label htmlFor="tokens" className="form-label">Addresses (one address per line; you may also add a space and custom amount per line to change the amount for that specific address)</label>
            <textarea placeholder={addressesPlaceholder} className="form-control" id="addresses" rows="10" name="addresses" value={airdropInfo.addresses} onChange={handleInputChange}></textarea>
          </div>
          <div className="col-12">
          <b>Summary</b>
          <hr />
          {airdropInfo.token &&
            <>
                <Balance token={airdropInfo.token} /> <br/>
            </>
          }
          Total airdrop amount: {totalAmount.toString()} <br/>
          Unique addresses: {addressCount}
          </div>
          {(airdropInfo.token && totalAmount) &&
            <Allowance token={airdropInfo.token} amount={totalAmount} airdropHandler={airdropIt} />
           }
          <div className="col-6">
          </div>
        </form>
        </div>
        </div>
        </AirdropProvider>
        </Web3Provider>
    );
}

export default AirdropComponent;